import { Injectable } from '@angular/core';
import { DepositModel } from '../../api/autogenerated/types.gen';
import { Deposit } from '../../types/Deposit';
import { SharedProfileData } from '../data/shared-profile-data';
import { IDepositsService } from './IDepositsService';
import { OpenApiService } from './open-api.service';

@Injectable({
  providedIn: 'root',
})
export class DepositsService implements IDepositsService {
  constructor(
    private openApiService: OpenApiService,
    private sharedProfileData: SharedProfileData,
  ) {}

  async getDeposits(): Promise<Deposit[]> {
    try {
      const user = this.sharedProfileData.getUserProfile();
      const response = await this.openApiService.getDeposits(user.businessId);

      console.log('Deposit object: ', response.data.at(0));
      console.log('Deposit ID: ', response.data.at(0)?.deposit_id);
      if (response.success && response.data) {
        return response.data.map((depositModel) =>
          this.convertDepositModelToDeposit(depositModel),
        );
      }
      return [];
    } catch (error) {
      console.error('Error fetching deposits:', error);
      throw error;
    }
  }

  async allocateDeposit(depositId: string, clientId: string): Promise<boolean> {
    try {
      const response = await this.openApiService.patchDeposit(
        depositId,
        clientId,
      );

      if (response.success) {
        return true;
      }
      throw new Error(response.error || 'Failed to allocate deposit');
    } catch (error) {
      console.error('Error allocating deposit:', error);
      throw error;
    }
  }

  private convertDepositModelToDeposit(depositModel: DepositModel): Deposit {
    return {
      id: depositModel.deposit_id || '',
      partnerId: depositModel.partner_id,
      currency: depositModel.currency || 'USD',
      amount: depositModel.amount || 0,
      source: depositModel.source || 'PARTNER_VBAN',
      status: depositModel.status || 'Unclassified',
      statementDescriptor: depositModel.statement_descriptor,
      b2bProvider: depositModel.b2b_provider,
      paymentMethod: depositModel.payment_method,
      receivedAt: depositModel.received_at || new Date().toISOString(),

      // Computed properties for UI display
      formattedAmount: this.formatCurrency(
        depositModel.amount || 0,
        depositModel.currency || 'USD',
      ),
      formattedReceivedDate: this.formatDate(
        depositModel.received_at || new Date().toISOString(),
      ),
      sourceLabel: this.getSourceLabel(depositModel.source || 'PARTNER_VBAN'),
      paymentMethodLabel: this.getPaymentMethodLabel(
        depositModel.payment_method,
      ),
    };
  }

  private formatCurrency(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  private getSourceLabel(source: string): string {
    switch (source) {
      case 'VENDOR_VBAN':
        return 'Vendor VBAN';
      case 'PARTNER_VBAN':
        return 'Partner VBAN';
      default:
        return source;
    }
  }

  private getPaymentMethodLabel(paymentMethod?: string): string {
    if (!paymentMethod) return 'N/A';

    switch (paymentMethod) {
      case 'ACH':
        return 'ACH';
      case 'RTP':
        return 'Real-Time Payment';
      case 'FEDWIRE':
        return 'Fedwire';
      case 'SWIFT':
        return 'SWIFT';
      case 'SEPA_CREDIT':
        return 'SEPA_CREDIT';
      case 'SEPA_INSTANT':
        return 'SEPA_INSTANT';
      case 'FPS':
        return 'FPS';
      case 'EFT':
        return 'EFT';
      default:
        return paymentMethod;
    }
  }
}
